/**
 * Each person's settings follow them (1.5.16 §6): language, appearance, text size, the last program tab and the
 * last Admin page are kept on the server per signed-in person (GET /api/me carries them, PUT /api/me/settings
 * takes a change). The same idea as "Hide my number" (src/lib/calling.ts):
 *
 *   - the device's copy applies at once on every start (src/lib/prefs.tsx) — no flash, and it works offline;
 *   - when /api/me answers, the server's settings win if they are not the ones this device last saw;
 *   - a change made here is on the device at once, marked unsent, and sent a moment later; while it is unsent
 *     it wins over the server; if the send fails it stays unsent and goes when the app is online again.
 *
 * Mounted once, with the signed-in app (src/App.tsx).
 */
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { MeResponse } from '@shared/admin';
import { MY_SETTING_KEYS, type MySettingKey, type MySettings, type MySettingsPatch } from '@shared/my-settings';
import { send, useMeExtras } from './admin';
import { ApiRequestError } from './api';
import { hasStoredPrefs, usePrefs, type Prefs } from './prefs';

/** A change waits this long before it is sent, so three presses under the gear are one request. */
export const SEND_DELAY_MS = 600;

/** The server's choices as a patch for this device: what is not chosen there (null), or still unsent here, is left alone. */
function fromServer(s: MySettings, keep: readonly MySettingKey[]): Partial<Prefs> {
  const out: Record<string, unknown> = {};
  for (const k of MY_SETTING_KEYS) if (s[k] !== null && !keep.includes(k)) out[k] = s[k];
  return out as Partial<Prefs>;
}

/**
 * One save, however its time is written: the instant an `updatedAt` names ("…:00Z", "…:00.000Z" and
 * "…-04:00" are one save), or the text itself when it is not a time at all.
 */
export function saveKey(updatedAt: string): number | string {
  const at = Date.parse(updatedAt);
  return Number.isFinite(at) ? at : updatedAt;
}


export function useSettingsSync(): void {
  const me = useMeExtras();
  const qc = useQueryClient();
  const { prefs, setPrefs, userId } = usePrefs();
  const latest = useRef(prefs);
  latest.current = prefs;
  // undefined: no answer yet, or the store could not be read — nothing is taken and nothing is sent
  const server = me.data?.settings;
  const [busy, setBusy] = useState(false);
  const [online, setOnline] = useState(0);
  /** The send that failed for the network: not tried again until something changes or the app is online again. */
  const [failed, setFailed] = useState<string | null>(null);
  /**
   * 1.5.22 §A — the sync never goes back and forth. For this person, since the app started: the saves this device
   * has already taken or been answered with (each is taken once — an answer that names one of them again, in
   * whatever form, changes nothing), and whether this device's own choices were already offered to an account
   * that had none (offered once — a refusal, or a server that goes on answering "none", is not asked again).
   */
  const done = useRef<{ user: string | null; saves: Set<number | string>; offered: boolean }>({ user: userId, saves: new Set(), offered: false });
  if (done.current.user !== userId) done.current = { user: userId, saves: new Set(), offered: false };

  useEffect(() => {
    const on = () => setOnline((n) => n + 1);
    window.addEventListener('online', on);
    return () => window.removeEventListener('online', on);
  }, []);

  // the server's answer
  useEffect(() => {
    if (server === undefined || prefs.settingsUnsent.length) return;
    const mine = done.current;
    if (server === null) {
      // nobody has saved for this person yet: what this device already has becomes the account's — offered once
      if (mine.offered || !hasStoredPrefs(userId)) return;
      mine.offered = true;
      setPrefs({ settingsUnsent: MY_SETTING_KEYS.filter((k) => prefs[k] !== null) });
      return;
    }
    const here = prefs.settingsAt === null ? null : saveKey(prefs.settingsAt);
    const there = saveKey(server.updatedAt);
    if (here !== null) mine.saves.add(here);
    // the save this device is in step with: nothing to do
    if (here === there) return;
    // a save this device has already had, and not a later one than it has now (an old answer come round again —
    // or one that cannot be placed in time at all): not taken a second time
    if (mine.saves.has(there) && !(typeof here === 'number' && typeof there === 'number' && there > here)) return;
    mine.saves.add(there);
    setPrefs({ ...fromServer(server, []), settingsAt: server.updatedAt, settingsUnsent: [] });
  }, [server, prefs, setPrefs, userId]);

  // what was changed here and is not on the server yet
  const unsent = prefs.settingsUnsent;
  const attempt = `${unsent.map((k) => `${k}=${String(prefs[k])}`).join('&')}|${online}|${me.dataUpdatedAt}`;
  useEffect(() => {
    if (!unsent.length || busy || server === undefined || failed === attempt) return undefined;
    const timer = window.setTimeout(() => {
      const now = latest.current;
      const patch: MySettingsPatch = Object.fromEntries(now.settingsUnsent.map((k) => [k, now[k]]));
      setBusy(true);
      send<MySettings>('/api/me/settings', 'PUT', patch)
        .then((out) => {
          // the account first, then the device: in between, the change is still "unsent" and so still wins
          qc.setQueryData<MeResponse>(['me-extras'], (d) => (d ? { ...d, settings: out } : d));
          const after = latest.current;
          const left = after.settingsUnsent.filter((k) => after[k] !== patch[k]);
          setPrefs({ ...fromServer(out, left), settingsAt: out.updatedAt, settingsUnsent: left });
          setFailed(null);
        })
        .catch((e: unknown) => {
          // refused (not signed in, no role): the account's settings stand. Anything else is the network: kept.
          if (e instanceof ApiRequestError && e.status >= 400 && e.status < 500) setPrefs({ settingsUnsent: [] });
          else setFailed(attempt);
        })
        .finally(() => setBusy(false));
    }, SEND_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [unsent.length, attempt, busy, failed, server === undefined, qc, setPrefs]); // eslint-disable-line react-hooks/exhaustive-deps
}
